/**
 * ANNUAIRE — ce que l'écran sait des règles, sans les décider (lot 2, étape 3).
 *
 * Module PUR (testé depuis ../backend/tests, préfixe `front-`). Les règles font
 * foi côté moteur (`backend/src/annuaire-validation.ts`, appelé par
 * `POST ged/annuaire/verifier` pendant la saisie) : l'écran ne fait ici que
 * deux choses qui ne peuvent pas attendre le serveur —
 *   · la MISE EN FORME PENDANT LA FRAPPE (NOM en majuscules, Prénom avec une
 *     majuscule à chaque mot : le contraste rend une inversion visible) ;
 *   · le FORMULAIRE PROPRE AU TYPE (REGLES §2.0) : quels champs montrer,
 *     lesquels sont obligatoires, pour que « Enregistrer » reste désactivé tant
 *     qu'il en manque.
 */

/* ── Mise en forme pendant la frappe (§2.1) ─────────────────────────────── */

/** `ben salem` → `BEN SALEM` — sans retirer l'espace que l'on est en train de taper. */
export function nomPendantFrappe(v: string): string {
  return v.replace(/\s{2,}/g, ' ').toLocaleUpperCase('fr');
}

/** `MOHAMED amine` → `Mohamed Amine`, `jean-marc` → `Jean-Marc`. */
export function prenomPendantFrappe(v: string): string {
  return v.replace(/\s{2,}/g, ' ').toLocaleLowerCase('fr')
    .replace(/(^|[\s-])(\p{L})/gu, (_m, sep: string, l: string) => sep + l.toLocaleUpperCase('fr'));
}

/* ── Les tuiles de création (§2.0, étape 1) ─────────────────────────────── */

export type Tuile = 'medecin' | 'professionnel' | 'laboratoire' | 'etablissement' | 'fournisseur' | 'interlocuteur' | 'ligne_service';

export const TUILES: { code: Tuile; libelle: string; aide: string; icone: string; permission: 'correspondant' | 'fournisseur' }[] = [
  { code: 'medecin', libelle: 'Médecin prescripteur', aide: 'Une seule fiche par médecin, ses lieux d’exercice s’y rattachent.', icone: 'users', permission: 'correspondant' },
  { code: 'professionnel', libelle: 'Autre professionnel de santé', aide: 'Dentiste, sage-femme, vétérinaire, paramédical…', icone: 'users', permission: 'correspondant' },
  { code: 'laboratoire', libelle: 'Laboratoire', aide: 'Correspondant, confrère ou sous-traitant — une seule fiche pour tous ses rôles.', icone: 'flask', permission: 'correspondant' },
  { code: 'etablissement', libelle: 'Établissement de santé', aide: 'Clinique, CHU, hôpital, centre.', icone: 'pin', permission: 'correspondant' },
  { code: 'fournisseur', libelle: 'Fournisseur', aide: 'Réactifs, matériel, services… avec sa catégorie d’achat.', icone: 'truck', permission: 'fournisseur' },
  { code: 'interlocuteur', libelle: 'Interlocuteur d’un fournisseur', aide: 'Commercial, SAV, ADV… rattaché à son fournisseur.', icone: 'handshake', permission: 'fournisseur' },
  { code: 'ligne_service', libelle: 'Ligne de service', aide: 'Siège, standard, service commande, SAV — un numéro qui n’est pas une personne.', icone: 'sitemap', permission: 'fournisseur' },
];

export const CATEGORIES_PROFESSIONNEL = [
  { code: 'dentiste', libelle: 'Dentiste' }, { code: 'sage_femme', libelle: 'Sage-femme' },
  { code: 'veterinaire', libelle: 'Vétérinaire' }, { code: 'pharmacien', libelle: 'Pharmacien' },
  { code: 'biologiste', libelle: 'Biologiste' }, { code: 'paramedical', libelle: 'Paramédical' }, { code: 'autre', libelle: 'Autre' },
];
export const SOUS_TYPES_ETABLISSEMENT = [
  { code: 'clinique', libelle: 'Clinique' }, { code: 'chu', libelle: 'CHU' }, { code: 'hopital', libelle: 'Hôpital' }, { code: 'centre', libelle: 'Centre' },
];
export const ROLES_LABORATOIRE = [
  { code: 'correspondant', libelle: 'Correspondant (nous confie des prélèvements)' },
  { code: 'sous_traitant', libelle: 'Sous-traitant (BIO24 lui envoie)' },
  { code: 'confrere', libelle: 'Confrère (échanges, prêt de réactifs)' },
  { code: 'fournisseur', libelle: 'Fournisseur (nous facture)' },
];
/** Tout laboratoire est correspondant et confrère (Ines, 20/10/2026) — même règle qu'au moteur (`annuaire-validation`). */
export const ROLES_SOCLE_LABORATOIRE = ['correspondant', 'confrere'];
export const rolesLaboratoire = (roles: string[] = []) => [...new Set([...roles, ...ROLES_SOCLE_LABORATOIRE])];
export const TITRES = ['Dr', 'Pr', 'M.', 'Mme'];

export const LIBELLE_TYPE: Record<string, string> = {
  fournisseur: 'Fournisseur', laboratoire: 'Laboratoire', etablissement_sante: 'Établissement de santé',
  pharmacie: 'Pharmacie', cabinet: 'Cabinet (lieu d’exercice)', organisme: 'Organisme', autre: 'Autre',
};
export const LIBELLE_ROLE: Record<string, string> = {
  fournisseur: 'Fournisseur', fabricant: 'Fabricant', correspondant: 'Correspondant', confrere: 'Confrère',
  sous_traitant: 'Sous-traitant', prestataire: 'Prestataire', organisme: 'Organisme',
};
export const LIBELLE_CATEGORIE: Record<string, string> = {
  medecin: 'Médecin', biologiste: 'Biologiste', pharmacien: 'Pharmacien', dentiste: 'Dentiste', veterinaire: 'Vétérinaire',
  sage_femme: 'Sage-femme', paramedical: 'Paramédical', interlocuteur_fournisseur: 'Interlocuteur fournisseur', autre: 'Autre',
};
export const LIBELLE_FIABILITE: Record<string, string> = {
  saisie_boxylab: 'Saisie BoXyLab', deduite: 'Déduite', confirmee: 'Confirmée', a_verifier: 'À vérifier', prenom_seul: 'Prénom seul', saisie_nafis: 'Saisie dans l’annuaire',
};
export const LIBELLE_CHAMP: Record<string, string> = {
  nom: 'NOM / Prénom', prenom: 'Prénom', specialite: 'Spécialité', code_cnam: 'Code CNAM', contact: 'Moyen de contact',
  telephone: 'Téléphone', email: 'Email', adresse: 'Adresse', matricule_fiscal: 'Matricule fiscal', categorie_achat: 'Catégorie d’achat',
  ville_code: 'Ville', responsable_per_id: 'Biologiste responsable', sous_type: 'Sous-type', fonction: 'Fonction', doublon: 'Doublon',
  fusion: 'Fusion', lieu_exercice: 'Lieu d’exercice', laboratoires: 'Laboratoires rattachés', code_postal: 'Code postal', numero_ordre: 'N° d’ordre',
  raison_sociale: 'Raison sociale', site_web: 'Site web', titre: 'Titre', categorie: 'Catégorie', roles: 'Rôles', type: 'Type', libelle: 'Libellé',
  affiliation: 'Rattachement', indicatif: 'Indicatif', nom_epouse: 'Nom d’épouse', nom_court: 'Nom court', activite: 'Activité',
};
export const libelleChamp = (c: string) => LIBELLE_CHAMP[c] || c;

/* ── Le formulaire propre au type (§2.0, étape 2) ───────────────────────── */

export interface Saisie {
  titre?: string; nom?: string; prenom?: string; nom_epouse?: string; categorie?: string; specialite?: string; code_cnam?: string;
  numero_ordre?: string; raison_sociale?: string; nom_court?: string; roles?: string[]; sous_type?: string; ville_code?: string;
  categorie_achat?: string; pays?: string; matricule_fiscal?: string; responsable_per_id?: string; adresse?: string; code_postal?: string;
  site_web?: string; activite?: string; org_id?: string; fonction?: string; libelle?: string; libelle_libre?: string; rattachee_a?: string;
  telephones?: { indicatif: string; numero: string; type?: string; principal?: boolean }[];
  emails?: { adresse: string; principal?: boolean; usages?: string[] }[];
  derogationCnam?: string;
  /** Médecin proposé comme échographiste dans MoMent (une coche, RQ 11/10/2026). */
  echographiste?: boolean;
}

/** Les champs MONTRÉS pour un type : aucun autre (critère §10 : le formulaire
 *  « Fournisseur » n'affiche ni CNAM ni spécialité, « Médecin » ni MF ni catégorie). */
export const CHAMPS_DU_TYPE: Record<Tuile, string[]> = {
  medecin: ['titre', 'nom', 'prenom', 'nom_epouse', 'specialite', 'echographiste', 'code_cnam', 'numero_ordre', 'contact'],
  professionnel: ['categorie', 'titre', 'nom', 'prenom', 'nom_epouse', 'specialite', 'contact'],
  laboratoire: ['raison_sociale', 'nom_court', 'roles', 'responsable_per_id', 'specialite', 'ville_code', 'adresse', 'code_postal', 'matricule_fiscal', 'site_web', 'rattachee_a', 'contact'],
  etablissement: ['raison_sociale', 'nom_court', 'sous_type', 'ville_code', 'adresse', 'code_postal', 'site_web', 'contact'],
  fournisseur: ['raison_sociale', 'nom_court', 'categorie_achat', 'pays', 'matricule_fiscal', 'activite', 'adresse', 'code_postal', 'ville_code', 'site_web', 'contact'],
  interlocuteur: ['org_id', 'titre', 'nom', 'prenom', 'fonction', 'contact'],
  ligne_service: ['org_id', 'libelle', 'libelle_libre', 'contact'],
};

const rempli = (v: unknown) => (Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '');
const aUnContact = (s: Saisie) => (s.telephones || []).some((t) => rempli(t.numero)) || (s.emails || []).some((e) => rempli(e.adresse));

/** Les champs obligatoires qui MANQUENT (le bouton Enregistrer en dépend). */
export function manquesSaisie(tuile: Tuile, s: Saisie): string[] {
  const m: string[] = [];
  const exiger = (champ: keyof Saisie) => { if (!rempli(s[champ])) m.push(champ); };
  switch (tuile) {
    case 'medecin':
      exiger('nom'); exiger('prenom'); exiger('specialite');
      if (!rempli(s.code_cnam) && String(s.derogationCnam || '').trim().length < 10) m.push('code_cnam');
      break;
    case 'professionnel':
      exiger('categorie'); exiger('nom'); exiger('prenom');
      if (['dentiste', 'biologiste'].includes(String(s.categorie))) exiger('specialite');
      break;
    case 'laboratoire': exiger('raison_sociale'); exiger('responsable_per_id'); exiger('ville_code'); exiger('specialite'); break;
    case 'etablissement': exiger('raison_sociale'); exiger('sous_type'); exiger('ville_code'); break;
    case 'fournisseur':
      exiger('raison_sociale'); exiger('categorie_achat'); exiger('pays');
      if ((s.pays || 'Tunisie') === 'Tunisie') exiger('matricule_fiscal');
      break;
    case 'interlocuteur':
      exiger('org_id'); exiger('fonction');
      if (!rempli(s.nom) && !rempli(s.prenom)) m.push('nom');
      break;
    case 'ligne_service': exiger('org_id'); exiger('libelle'); break;
  }
  if (!aUnContact(s)) m.push('contact');
  return m;
}

/** Le corps envoyé au moteur, et la route qui le reçoit. */
export function requeteCreation(tuile: Tuile, s: Saisie): { route: string; corps: Record<string, unknown> } {
  const coordonnees = {
    telephones: (s.telephones || []).filter((t) => rempli(t.numero)).map((t) => ({ indicatif: t.indicatif || '+216', numero: t.numero, type: t.type || undefined, principal: !!t.principal })),
    emails: (s.emails || []).filter((e) => rempli(e.adresse)).map((e) => ({ adresse: e.adresse, principal: !!e.principal, usages: e.usages || [] })),
  };
  const garder = (champs: string[]) => Object.fromEntries(champs.filter((c) => rempli((s as Record<string, unknown>)[c])).map((c) => [c, (s as Record<string, unknown>)[c]]));
  switch (tuile) {
    case 'medecin':
      return { route: 'ged/annuaire/personnes', corps: { categorie: 'medecin', ...garder(['titre', 'nom', 'prenom', 'nom_epouse', 'specialite', 'code_cnam', 'numero_ordre']), echographiste: !!s.echographiste, ...coordonnees,
        ...(!rempli(s.code_cnam) && rempli(s.derogationCnam) ? { derogations: [{ champ: 'code_cnam', motif: s.derogationCnam }] } : {}) } };
    case 'professionnel':
      return { route: 'ged/annuaire/personnes', corps: { ...garder(['categorie', 'titre', 'nom', 'prenom', 'nom_epouse', 'specialite']), ...coordonnees } };
    case 'interlocuteur':
      return { route: 'ged/annuaire/personnes', corps: { categorie: 'interlocuteur_fournisseur', ...garder(['titre', 'nom', 'prenom']), ...coordonnees,
        affiliation: { org_id: s.org_id, fonction: s.fonction, role: 'interlocuteur' } } };
    case 'laboratoire':
      return { route: 'ged/annuaire/organisations', corps: { type: 'laboratoire', roles: rolesLaboratoire(s.roles), ...garder(['raison_sociale', 'nom_court', 'responsable_per_id', 'specialite', 'ville_code', 'adresse', 'code_postal', 'matricule_fiscal', 'site_web', 'rattachee_a']), ...coordonnees,
        ...((s.roles || []).includes('fournisseur') ? { categorie_achat: 'laboratoire_sous_traitance_confrere' } : {}) } };
    case 'etablissement':
      return { route: 'ged/annuaire/organisations', corps: { type: 'etablissement_sante', roles: ['correspondant'], ...garder(['raison_sociale', 'nom_court', 'sous_type', 'ville_code', 'adresse', 'code_postal', 'site_web']), ...coordonnees } };
    case 'fournisseur':
      return { route: 'ged/annuaire/organisations', corps: { type: 'fournisseur', roles: ['fournisseur'], pays: s.pays || 'Tunisie', ...garder(['raison_sociale', 'nom_court', 'categorie_achat', 'matricule_fiscal', 'activite', 'adresse', 'code_postal', 'ville_code', 'site_web']), ...coordonnees } };
    case 'ligne_service':
      return { route: 'ged/annuaire/lignes-service', corps: { ...garder(['org_id', 'libelle', 'libelle_libre']), ...coordonnees } };
  }
}

/** Le corps de vérification (`POST ged/annuaire/verifier`) : même saisie, sans écrire. */
export function requeteVerification(tuile: Tuile, s: Saisie): Record<string, unknown> | null {
  if (tuile === 'ligne_service') return null;
  const { corps } = requeteCreation(tuile, s);
  const entite = ['medecin', 'professionnel', 'interlocuteur'].includes(tuile) ? 'personne' : 'organisation';
  return { entite, ...corps };
}

/** Identifiant d'annuaire → nature de la fiche. */
export const estPersonne = (id: string | null | undefined) => /^PER-\d+$/.test(String(id || ''));
export const estOrganisation = (id: string | null | undefined) => /^ORG-\d+$/.test(String(id || ''));
